import { useAppStore } from '@/store/store';
import { DimensionUnit, WeightUnit, FreightMode, ThemeMode } from '@freight-cbm/shared-types';
import { Select } from 'calc-controls';
import { CurrencySelect } from './CurrencySelect';
import { replayOnboardingTour } from './OnboardingTour';
import { useAlert } from '@ascend/alert';
import { useEffect, useRef } from 'react';
import { X, Sun, Moon, Monitor, GraduationCap, Info } from 'lucide-react';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  onAboutClick: () => void;
}

export function SettingsModal({ open, onClose, onAboutClick }: SettingsModalProps) {
  const preferences = useAppStore((s) => s.preferences);
  const setPreference = useAppStore((s) => s.setPreference);
  const clearHistory = useAppStore((s) => s.clearHistory);
  const calculations = useAppStore((s) => s.calculations);
  const { confirm } = useAlert();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-bg-primary)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          maxWidth: 480,
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)' }}>Settings</h2>
          <button type="button" className="btn btn-sm" onClick={onClose} aria-label="Close settings"><X size={16} /></button>
        </div>

        <section style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>Defaults</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <Select
              label="Dimension Unit"
              options={[
                { value: DimensionUnit.Cm, label: 'cm' },
                { value: DimensionUnit.Mm, label: 'mm' },
                { value: DimensionUnit.M, label: 'm' },
                { value: DimensionUnit.In, label: 'in' },
              ]}
              value={preferences.defaultDimUnit}
              onChange={(v) => setPreference('defaultDimUnit', v as DimensionUnit)}
            />
            <Select
              label="Weight Unit"
              options={[
                { value: WeightUnit.Kg, label: 'kg' },
                { value: WeightUnit.Lb, label: 'lb' },
              ]}
              value={preferences.defaultWeightUnit}
              onChange={(v) => setPreference('defaultWeightUnit', v as WeightUnit)}
            />
            <Select
              label="Freight Mode"
              options={[
                { value: FreightMode.Air, label: 'Air' },
                { value: FreightMode.OceanLCL, label: 'Ocean LCL' },
                { value: FreightMode.Courier, label: 'Courier' },
              ]}
              value={preferences.defaultFreightMode}
              onChange={(v) => setPreference('defaultFreightMode', v as FreightMode)}
            />
            <CurrencySelect label="Currency" value={preferences.currency} onChange={(v) => setPreference('currency', v)} />
          </div>
        </section>

        <section style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>Theme</h3>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {([ThemeMode.Light, ThemeMode.Dark, ThemeMode.System] as const).map((t) => (
              <button
                type="button"
                key={t}
                className={preferences.theme === t ? 'btn btn-primary' : 'btn'}
                onClick={() => setPreference('theme', t)}
                aria-pressed={preferences.theme === t}
                style={{ flex: 1 }}
              >
                {t === ThemeMode.Light ? <><Sun size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> Light</> : t === ThemeMode.Dark ? <><Moon size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> Dark</> : <><Monitor size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> System</>}
              </button>
            ))}
          </div>
        </section>

        <section style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>Demo Data</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={preferences.showDemoOption}
                onChange={(e) => setPreference('showDemoOption', e.target.checked)}
              />
              Show Load Demo option in header
            </label>
            <button
              type="button"
              className="btn"
              onClick={() => {
                useAppStore.getState().clearPackages();
                useAppStore.getState().setMode(FreightMode.Air);
                useAppStore.getState().setRatePerKg(0);
                useAppStore.getState().setRatePerCBM(0);
                useAppStore.getState().setRatePerRevenueTon(0);
                useAppStore.getState().setAccessorials([]);
              }}
              style={{ color: 'var(--color-error)' }}
            >
              Clear Demo Data
            </button>
          </div>
        </section>

        <section style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>Help</h3>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
            New here? Replay the quick product tour to refresh how CBM, chargeable weight, and freight modes work.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              replayOnboardingTour();
              onClose();
            }}
          >
            <GraduationCap size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} />
            Replay onboarding tour
          </button>
        </section>

        <section>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>Data</h3>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
            {calculations.length} saved calculation{calculations.length !== 1 ? 's' : ''}
          </p>
          {calculations.length > 0 && (
            <button
              type="button"
              className="btn"
              onClick={async () => {
                const ok = await confirm({
                  title: 'Clear History',
                  message: `Delete all ${calculations.length} saved calculation${calculations.length !== 1 ? 's' : ''}? This cannot be undone.`,
                  danger: true,
                  confirmLabel: 'Clear',
                });
                if (ok) clearHistory();
              }}
              style={{ color: 'var(--color-error)' }}
            >
              Clear History
            </button>
          )}
        </section>

        <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: 'var(--space-4) 0' }} />

        <section>
          <button
            type="button"
            className="btn"
            onClick={onAboutClick}
          >
            <Info size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} />
            About
          </button>
        </section>
      </div>
    </div>
  );
}
